import {
  type FC,
  type Ref,
  type TextareaHTMLAttributes,
  useCallback,
  useMemo,
} from "react";
import { useFormContext } from "react-hook-form";

type InputSize = "xs" | "sm" | "md" | "lg" | "xl";

export type TextareaUIProps = {
  registerName: `${string}` | `${string}.${string}` | `${string}.${number}`;
  validatable?: boolean;
  label?: string;
  inputSize?: InputSize;
  color?:
    | "primary"
    | "secondary"
    | "info"
    | "accent"
    | "success"
    | "warning"
    | "error"
    | "default";
  /** 外部からDOMにアクセスするためのref（react-hook-formのrefと両立） */
  textareaRef?: Ref<HTMLTextAreaElement>;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "ref">;

export const TextareaUI: FC<TextareaUIProps> = ({
  className = "",
  registerName,
  label = "",
  inputSize = "md",
  color = "default",
  textareaRef,
  children,
  ...rest
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const error = errors[registerName];

  const { ref: rhfRef, ...registerProps } = register(registerName);

  // react-hook-form の ref と外部の ref を両立させる
  const mergedRef = useCallback(
    (element: HTMLTextAreaElement | null) => {
      rhfRef(element);
      if (textareaRef) {
        if (typeof textareaRef === "function") {
          textareaRef(element);
        } else if (textareaRef && "current" in textareaRef) {
          (
            textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>
          ).current = element;
        }
      }
    },
    [rhfRef, textareaRef],
  );

  const getColor = useMemo(
    () =>
      ({
        primary: "textarea-primary",
        secondary: "textarea-secondary",
        info: "textarea-info",
        accent: "textarea-accent",
        success: "textarea-success",
        warning: "textarea-warning",
        error: "textarea-error",
        default: "",
      })[color],
    [color],
  );

  const getSize = useMemo(
    () =>
      ({
        xs: "textarea-xs",
        sm: "textarea-sm",
        md: "textarea-md",
        lg: "textarea-lg",
        xl: "textarea-xl",
      })[inputSize],
    [inputSize],
  );

  return (
    <div className="form-control w-full">
      {label && (
        <label className="label">
          <span className="label-text">{label}</span>
        </label>
      )}
      <textarea
        placeholder="Type here"
        className={`textarea textarea-bordered w-full ${className} ${getSize} ${
          error ? "textarea-error" : getColor
        }`}
        {...rest}
        {...registerProps}
        ref={mergedRef}
      />
      {error && (
        <label className="label">
          <span className="label-text-alt text-error">
            {String(error.message || "")}
          </span>
        </label>
      )}
    </div>
  );
};
